import React from "react";
import { Link } from "react-router-dom";
import {
  IndianRupee,
  FileCheck,
  CalendarClock,
  Receipt,
  ShieldAlert,
  Wallet,
  ArrowRight,
  Calculator,
  ShieldCheck,
  Percent
} from "lucide-react";

export default function PayrollAndCompliance() {
  const poppinsStyle = { fontFamily: "'Poppins', sans-serif" };
  const robotoStyle = { fontFamily: "'Roboto', sans-serif" };

  return (
    <div className="min-h-screen bg-white" style={robotoStyle}>
      {/* Hero Section */}
      <div className="bg-white py-16 px-6 border-b border-slate-50">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-medium mb-6" style={poppinsStyle}>
            <Calculator size={16} />
            Statutory-Ready Payroll
          </div>
          
          <h1 className="text-3xl md:text-4xl font-medium text-gray-900 mb-6 leading-tight" style={poppinsStyle}>
            Automated Payroll & <br />
            Statutory Compliance Management
          </h1>
          
          <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Eliminate calculation errors and manual data entry. Process salaries in clicks 
            while automatically handling PF, ESI, TDS, and Professional Tax with 100% accuracy.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-20">
        {/* Features + Insights Grid */}
        <div className="grid md:grid-cols-2 gap-12 items-start mt-16 mb-20">
          <div className="space-y-8">
            <h2 className="text-xl font-medium text-gray-900 border-l-4 border-indigo-500 pl-4" style={poppinsStyle}>
              Core Payroll Engine
            </h2>
            
            <div className="grid gap-8">
              <Feature 
                icon={<IndianRupee size={20} />} 
                title="Automated Disbursement" 
                desc="One-click salary processing including earnings, deductions, bonuses, and arrears."
                poppins={poppinsStyle}
              />
              <Feature 
                icon={<Percent size={20} />} 
                title="Tax & TDS Planning" 
                desc="Automated income tax projections and investment declaration workflows for employees."
                poppins={poppinsStyle}
              />
              <Feature 
                icon={<Receipt size={20} />} 
                title="Full & Final Settlements" 
                desc="Streamlined exit payroll including leave encashment, notice pay, and gratuity."
                poppins={poppinsStyle}
              />
            </div>
          </div>

          {/* Right: Rich Informational Sidebar */}
          <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100">
            <h3 className="text-lg font-medium text-gray-900 mb-6" style={poppinsStyle}>Compliance Readiness</h3>
            <div className="space-y-4">
              <ComplianceItem label="PF & ESI Returns" status="Automated" />
              <ComplianceItem label="Professional Tax (PT)" status="State-wise" />
              <ComplianceItem label="Form 16 Generation" status="Bulk Export" />
              <ComplianceItem label="LWF Contributions" status="Managed" />
              <ComplianceItem label="Digital Signature (DSC)" status="Supported" />
            </div>
            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="text-xs text-slate-500 leading-relaxed italic">
                Our engine is updated in real-time with the latest government notifications 
                and budget changes to ensure your business remains audit-ready.
              </p>
            </div>
          </div>
        </div>

        {/* Rich Content Sections */}
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-12">
          <InfoBlock 
            title="Strategic Financial Insights"
            content="Gain deep visibility into your workforce costs with detailed analytics. Generate monthly salary registers, variance reports, and department-wise cost center allocations to help finance teams plan better."
            poppins={poppinsStyle}
          />
          <InfoBlock 
            title="Employee Empowerment"
            content="Reduce HR queries by providing a self-service portal. Employees can download payslips, view tax computations, submit investment proofs, and track reimbursement status on the go."
            poppins={poppinsStyle}
          />
          <InfoBlock 
            title="Secure Audit Trails"
            content="Maintain an immutable record of every salary change, bonus approval, and tax adjustment. Our system logs every transaction with time-stamps and user IDs for total transparency."
            poppins={poppinsStyle}
          />
          <InfoBlock 
            title="Statutory Adherence"
            content="We manage the complexities of Indian statutory laws including the Wage Code. From attendance integration to payslip distribution, every step is built to be legally compliant."
            poppins={poppinsStyle}
          />
        </div>

        {/* CTA Section */}
        <div className="text-center pt-20 mt-10 border-t border-slate-100">
          <h3 className="text-xl font-medium text-gray-900 mb-2" style={poppinsStyle}>Ready to experience zero-error payroll?</h3>
          <p className="text-sm text-gray-500 mb-8">Join modern finance teams automating their compliance today.</p>
          
          <div className="flex justify-center">
            <Link 
              to="/login"
              className="group flex items-center gap-3 bg-gray-900 hover:bg-indigo-600 text-white px-6 py-2.5 rounded-full transition-all duration-300 shadow-md"
            >
              <div className="text-left">
                <p className="text-[9px] uppercase tracking-widest opacity-70 leading-none mb-1" style={poppinsStyle}>Access Suite</p>
                <p className="text-sm font-semibold leading-none" style={poppinsStyle}>Get Started Now</p>
              </div>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Helper Components */
function Feature({ icon, title, desc, poppins }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
        {icon}
      </div>
      <div>
        <h4 className="font-medium text-gray-900 text-base" style={poppins}>{title}</h4>
        <p className="text-sm text-gray-600 mt-1 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function ComplianceItem({ label, status }) {
  return (
    <div className="bg-white p-4 rounded-xl flex items-center justify-between border border-slate-100 shadow-sm">
      <div className="flex items-center gap-3">
        <ShieldCheck className="text-emerald-500" size={18} />
        <span className="text-sm font-medium text-slate-700">{label}</span>
      </div>
      <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-widest">{status}</span>
    </div>
  );
}

function InfoBlock({ title, content, poppins }) {
  return (
    <div className="space-y-3">
      <h3 className="text-lg font-medium text-gray-900" style={poppins}>{title}</h3>
      <p className="text-sm text-gray-600 leading-relaxed">{content}</p>
    </div>
  );
}